<template>
  <div :class="['flex items-center w-full relative', { error: validation?.[fcProp]?.$invalid }, containerClass]">
    <template v-if="hasPrefix">
      <slot name="prefix" />
    </template>
    <input
      v-if="type !== 'textarea' && type !== 'decimal'"
      v-model.trim="value"
      :type="isShowingPassword"
      :placeholder="placeholder"
      :maxlength="maxlength"
      :disabled="(isDisabled || disabled)"
      v-maska
      :data-maska="cMask"
      :data-maska-tokens="maskTokens"
      :data-maska-reversed="maskReversed"
      :class="['ui-input peer', {
        'px-2 py-1': size === 'small',
        'px-3 py-3': !size || size === 'medium',
        'px-4 py-5': size === 'large',
        'border-l-0 border-t-0 border-r-0 border-b border-neutral-400/50': simple
      }]"
      v-bind="$attrs"
    />
    <textarea
      v-if="type === 'textarea' && !autoResize"
      v-model="value"
      :placeholder="placeholder"
      :disabled="(isDisabled || disabled)"
      :rows="rows"
      :maxlength="maxlength"
      class="ui-input peer p-3 h-full grow resize-none"
      v-bind="$attrs"
    />
    <span
      v-if="type === 'textarea' && autoResize"
      v-once
      role="textbox"
      ref="contenteditable"
      :placeholder="placeholder"
      :contenteditable="!(isDisabled || disabled)"
      :class="['ui-input block peer overflow-y-auto whitespace-pre-line', {
        'px-2 py-1': size === 'small',
        'px-3 py-3': !size || size === 'medium',
        'px-4 py-5': size === 'large'
      }]"
      v-bind="$attrs"
      v-text="value"
      @input="updateValue"
    ></span>
    <input
      v-if="type === 'decimal'"
      v-model="value"
      type="number"
      :step="0.1"
      inputmode="decimal"
      :placeholder="placeholder"
      :maxlength="maxlength"
      :disabled="(isDisabled || disabled)"
      :class="['ui-input peer', {
        'px-2 py-1': size === 'small',
        'px-3 py-3': !size || size === 'medium',
        'px-4 py-5': size === 'large',
        'border-l-0 border-t-0 border-r-0 border-b border-neutral-400/50': simple
      }]"
      v-bind="$attrs"
    />

    <span
      v-if="togglePassword"
      class="ui-input-eye ui-suffix-icon cursor-pointer"
      @click="showPassword = !showPassword"
    >
      <UiIcon :size="24" class="text-black dark:text-neutral-400" :icon="showPassword ? 'eye-off' : 'eye'" />
    </span>
    <span v-if="hasSuffix" class="ui-suffix-icon">
      <slot name="suffix" class="pr-4" />
    </span>
    <div v-if="Number(maxlength) > 0" class="text-neutral-500 absolute -bottom-0.5 right-1 text-sm">{{ value ? `${value}`.length : 0 }}/{{ maxlength }}</div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, useSlots, watch } from 'vue'
import UiIcon from './icon.vue'

defineOptions({
  inheritAttrs: false
})

const props = defineProps<{
  autoResize?: boolean
  modelValue?: string | number
  placeholder?: string
  prefix?: string
  suffix?: string
  type?: string
  textTransform?: string
  disabled?: boolean
  maxlength?: number | string
  simple?: boolean
  rows?: number | string
  error?: any
  mask?: string | string[]
  maskTokens?: string
  maskReversed?: boolean
  togglePassword?: boolean
  size?: 'small' | 'medium' | 'large'
  containerClass?: string
}>()

const slots = useSlots()
const emit = defineEmits(['update:modelValue'])

const isDisabled = inject('form-disabled', false)
const showPassword = ref(false)

const isShowingPassword = computed(() => !props.togglePassword ? props.type : (showPassword.value ? 'text' : 'password'))

// const currentLength = computed(() => `${String(props.modelValue).length}`)
const validation = inject('validation', {})
const fcProp = inject('fc-prop', '')

const value = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const hasPrefix = computed(() => !!slots.prefix || !!props.prefix || false)
const hasSuffix = computed(() => !!slots.suffix || !!props.suffix || false)

const cMask = computed(() => (Array.isArray(props.mask) ? JSON.stringify(props.mask) : props.mask))

const contenteditable = ref<HTMLSpanElement | null>(null)
watch(
  () => props.modelValue,
  (val) => {
    if (!contenteditable.value || contenteditable.value === document.activeElement) {
      return
    }
    contenteditable.value.textContent = `${val}`
  }
)

const updateValue = (event: Event) => {
  const target = event.target as HTMLSpanElement
  const newValue = target.textContent || ''
  emit('update:modelValue', newValue)
}
</script>

<style lang="scss">
.ui-input {
  @apply box-border rounded-md border border-neutral-400/50 bg-[#fdfdfd] text-sm leading-5 text-[#212121] outline-none transition-all placeholder:text-[#bbb] focus:border-[--c-primary-600] disabled:text-[#79767A] dark:border-[#2C2C2C] dark:bg-[#2C2C2C] dark:text-[#FDFDFD] dark:placeholder:text-[#D2D2D2] dark:focus:border-[--c-primary-600] dark:disabled:bg-[#414141];

  &:hover {
    @apply bg-[#f8f6fb] dark:bg-[#343434];
  }

  &:focus {
    @apply bg-[#f6f2f9] dark:bg-[#383838];
  }

  &:disabled {
    @apply bg-[#e6e1e6] dark:bg-[#414141];
  }

  &[contenteditable]::empty::before {
    content: attr(placeholder);
    @apply placeholder:text-[#bbb] dark:placeholder:text-[#D2D2D2];
  }
}

.ui-input-eye {
  @apply peer-focus:text-primary;
}

.ui-suffix-icon {
  @apply absolute top-0 right-0 px-2 h-full flex items-center;
}

.error > {
  .ui-input {
    @apply border-[#FF4980] bg-[#fcf2f6] dark:bg-[#383838];
  }

  .ui-input-eye {
    @apply text-[#FF4980]
  }
}

.error {
  .ui-input  {
    @apply border-red-400 bg-red-50 dark:bg-transparent;
  }
}

.success {
  .ui-input  {
    @apply border-emerald-300 bg-emerald-50 dark:bg-transparent;
  }
}
</style>
